import { useMemo, useState } from "react";
import { Check, Building2, MapPin, Shield, Route } from "lucide-react";
import { formatMoney } from "../../utils/formatMoney";

function formatPolicyType(policyType) {
  if (policyType === "okada_rider") return "Motor Rider";
  return (
    policyType?.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) ||
    "Insurance"
  );
}

function formatGeography(geography) {
  if (geography === "both") return "Regional + Intl";
  if (!geography) return null;
  return geography.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function useFallbackLogo(item) {
  return useMemo(() => {
    const logoUrl = item?.companyLogo;
    if (typeof logoUrl !== "string" || logoUrl.trim() === "") return true;
    return /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(
      logoUrl.toLowerCase().trim()
    );
  }, [item]);
}

export default function InsuranceCard({
  item,
  isSelected,
  onToggleSelect,
  showCompareOption = false,
}) {
  const [imageError, setImageError] = useState(false);
  const showFallback = useFallbackLogo(item) || imageError;
  const providerName =
    item.insurance_provider?.company_name || item.provider || "Insurance Provider";
  const geographyLabel = formatGeography(item.geography);
  const routeCount = item.originalPolicy?.coverage_durations?.length || 0;
  const covers = [...new Set((item.perks || []).filter(Boolean))].slice(0, 2);

  const handleCompareToggle = (event) => {
    event.stopPropagation();
    if (onToggleSelect) onToggleSelect(item);
  };

  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${
        isSelected
          ? "border-emerald-500 ring-2 ring-emerald-500/25"
          : "border-gray-200 hover:border-emerald-200"
      }`}
    >
      <div className="h-1 shrink-0 bg-emerald-500" />

      {showCompareOption && (
        <div className="absolute top-3 right-3 z-10" data-compare-btn>
          <button
            type="button"
            onClick={handleCompareToggle}
            data-compare-btn
            className={`grid h-8 w-8 place-items-center rounded-lg border-2 transition-all ${
              isSelected
                ? "border-emerald-500 bg-emerald-500 text-white"
                : "border-gray-200 bg-white text-gray-400 hover:border-emerald-500"
            }`}
            aria-label={isSelected ? "Remove from comparison" : "Add to comparison"}
          >
            {isSelected && <Check className="h-4 w-4" />}
          </button>
        </div>
      )}

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex min-w-0 items-center gap-3">
          {showFallback ? (
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-50 ring-1 ring-emerald-100">
              <Building2 className="h-6 w-6 text-emerald-600" />
            </div>
          ) : (
            <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-gray-50 ring-1 ring-gray-100 p-1">
              <img
                src={item.companyLogo}
                alt=""
                className="h-full w-full object-contain"
                onError={() => setImageError(true)}
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-gray-900 sm:text-base">
              {providerName}
            </h3>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {item.policyName || formatPolicyType(item.policy_type)}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-100">
            {formatPolicyType(item.policy_type)}
          </span>
          {geographyLabel && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-semibold text-gray-600 ring-1 ring-gray-100">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              {geographyLabel}
            </span>
          )}
        </div>

        <div className="mt-4">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
            From
          </p>
          <p className="text-xl font-bold text-gray-900">
            {formatMoney(item.premiumMin || 0)}
          </p>
          {item.premiumMax > item.premiumMin && (
            <p className="text-xs text-gray-500">
              up to {formatMoney(item.premiumMax)}
            </p>
          )}
        </div>

        {covers.length > 0 && (
          <ul className="mt-3 space-y-1">
            {covers.map((cover) => (
              <li key={cover} className="flex min-w-0 items-center gap-1.5 text-xs text-gray-600">
                <Shield className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
                <span className="truncate">{cover}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-auto flex flex-wrap gap-x-3 gap-y-1 pt-3 text-[11px] text-gray-500">
          {routeCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Route className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />
              {routeCount} {routeCount === 1 ? "route" : "routes"}
            </span>
          )}
          {item.maxClaims != null && <span>Max {item.maxClaims} claims</span>}
        </div>
      </div>

      <div className="px-4 pb-4 sm:px-5">
        <button
          type="button"
          className="flex w-full items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 group-hover:bg-emerald-700"
        >
          View Details
        </button>
      </div>
    </article>
  );
}
